import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Plus, Beaker } from "lucide-react";
import { toast } from "sonner";

export default function LabCatalogue() {
  const [tests, setTests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", category: "General", price: "", discounted_price: "", sample_type: "Blood", turnaround_hours: 24, fasting_required: false });
  const load = () => api.get("/tests").then((r) => setTests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tests", { ...form, price: Number(form.price), discounted_price: form.discounted_price ? Number(form.discounted_price) : null, turnaround_hours: Number(form.turnaround_hours) });
      toast.success("Test added");
      setOpen(false);
      setForm({ code: "", name: "", category: "General", price: "", discounted_price: "", sample_type: "Blood", turnaround_hours: 24, fasting_required: false });
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  return (
    <DashboardLayout role="lab_admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink">Test Catalogue</h1>
          <p className="text-ink/60">Manage tests and packages available for booking.</p>
        </div>
        <Button data-testid="add-test-btn" onClick={() => setOpen(true)} className="bg-ink hover:bg-ink/90 text-white"><Plus className="w-4 h-4 mr-2" />Add Test</Button>
      </div>

      <Card className="mt-6 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_0.8fr_0.8fr_0.6fr] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40">
          <div>Test</div><div>Category</div><div>Price</div><div>TAT</div><div>Fasting</div>
        </div>
        {tests.length === 0 && <div className="p-10 text-center text-ink/50">No tests yet.</div>}
        {tests.map((t) => (
          <div key={t.id} data-testid={`test-${t.code}`} className="grid grid-cols-[2fr_1fr_0.8fr_0.8fr_0.6fr] gap-3 px-5 py-3 items-center border-b border-border hover:bg-muted/40">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-efgh-green/10 text-efgh-green grid place-items-center"><Beaker className="w-4 h-4" /></div>
              <div>
                <div className="font-medium text-ink">{t.name}</div>
                <div className="text-xs text-ink/50">{t.code} · {t.sample_type}</div>
              </div>
            </div>
            <div className="text-sm text-ink/70">{t.category}</div>
            <div className="font-heading font-semibold text-ink">₹{t.discounted_price || t.price}{t.discounted_price ? <span className="ml-1 text-xs text-ink/40 line-through font-normal">₹{t.price}</span> : null}</div>
            <div className="text-sm text-ink/60">{t.turnaround_hours} hrs</div>
            <div>{t.fasting_required ? <span className="pill pill-borderline">Fasting</span> : <span className="pill pill-normal">No</span>}</div>
          </div>
        ))}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogTitle>Add a new test</DialogTitle>
          <form onSubmit={add} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input required data-testid="test-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div className="col-span-2"><Label>Name</Label><Input required data-testid="test-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Price ₹</Label><Input required type="number" data-testid="test-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div><Label>Discounted ₹</Label><Input type="number" value={form.discounted_price} onChange={(e) => setForm({ ...form, discounted_price: e.target.value })} /></div>
              <div><Label>Sample</Label><Input value={form.sample_type} onChange={(e) => setForm({ ...form, sample_type: e.target.value })} /></div>
              <div><Label>Turnaround (hrs)</Label><Input type="number" value={form.turnaround_hours} onChange={(e) => setForm({ ...form, turnaround_hours: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink/80">
              <input type="checkbox" checked={form.fasting_required} onChange={(e) => setForm({ ...form, fasting_required: e.target.checked })} /> Fasting required
            </label>
            <Button type="submit" className="w-full bg-ink hover:bg-ink/90 text-white" data-testid="test-submit">Add test</Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
